//#region src/latest.ts
/**
* Creates a runner that keeps only the newest input.
*
* @example
* const runner = createLatest<string>(async (text, signal) => cache.set(text, await rephrase(text, signal)), { debounceMs: 600 })
* runner.push('hello')
* runner.push('hello world') // aborts the run for 'hello'
*/
function createLatest(run, options = {}) {
	const debounceMs = options.debounceMs ?? 0;
	if (!Number.isFinite(debounceMs) || debounceMs < 0) throw new Error("Debounce must be finite and nonnegative");
	const closed = new AbortController();
	const key = options.key ?? ((value) => value);
	const none = Symbol("none");
	let lastKey = none;
	let timer;
	let active;
	let running;
	let waiting = Promise.withResolvers();
	let pending = false;
	function settleIfIdle() {
		if (!pending && !running) {
			waiting.resolve();
			waiting = Promise.withResolvers();
		}
	}
	function start(value) {
		pending = false;
		const controller = new AbortController();
		active = controller;
		const signal = AbortSignal.any([controller.signal, closed.signal]);
		running = (async () => {
			try {
				await run(value, signal);
			} catch (error) {
				if (!signal.aborted) options.onError?.(error);
			} finally {
				if (active === controller) {
					active = void 0;
					running = void 0;
				}
				settleIfIdle();
			}
		})();
	}
	function push(value) {
		if (closed.signal.aborted) return;
		const next = key(value);
		if (Object.is(next, lastKey)) return;
		lastKey = next;
		clearTimeout(timer);
		active?.abort("Superseded by a newer input");
		active = void 0;
		running = void 0;
		pending = true;
		if (debounceMs > 0) timer = setTimeout(start, debounceMs, value);
		else start(value);
	}
	function cancelPending() {
		if (!pending || running) return;
		clearTimeout(timer);
		pending = false;
		lastKey = none;
		settleIfIdle();
	}
	function close(reason = "Runner closed") {
		if (closed.signal.aborted) return;
		clearTimeout(timer);
		pending = false;
		closed.abort(reason);
		active = void 0;
		running = void 0;
		settleIfIdle();
	}
	options.signal?.addEventListener("abort", () => close(typeof options.signal?.reason === "string" ? options.signal.reason : void 0), {
		once: true,
		signal: closed.signal
	});
	if (options.signal?.aborted) close();
	return {
		push,
		cancelPending,
		idle: () => pending || running ? waiting.promise : Promise.resolve(),
		close
	};
}
//#endregion
//#region src/queue.ts
function createQueue(options) {
	const queue = [];
	let draining = false;
	const internalEventListeners = {
		enqueue: [],
		dequeue: [],
		process: [],
		error: [],
		result: [],
		drain: []
	};
	const internalHandlerEventListeners = {};
	function on(eventName, listener) {
		internalEventListeners[eventName].push(listener);
	}
	function emit(eventName, ...params) {
		internalEventListeners[eventName].forEach((listener) => listener(...params));
	}
	function onHandlerEvent(eventName, listener) {
		internalHandlerEventListeners[eventName] = internalHandlerEventListeners[eventName] || [];
		internalHandlerEventListeners[eventName].push(listener);
	}
	function emitHandlerEvent(eventName, ...params) {
		(internalHandlerEventListeners[eventName] || []).forEach((listener) => listener(...params));
	}
	function enqueue(payload) {
		queue.push(payload);
		emit("enqueue", payload, queue.length);
		if (!draining) {
			draining = true;
			drain();
		}
	}
	function clear() {
		queue.length = 0;
	}
	async function drain() {
		while (queue.length > 0) {
			const payload = queue.shift();
			emit("dequeue", payload, queue.length);
			for (const handler of options.handlers) {
				emit("process", payload, handler);
				try {
					emit("result", payload, await handler({
						data: payload,
						emit: emitHandlerEvent
					}), handler);
				} catch (err) {
					emit("error", payload, err, handler);
					continue;
				}
			}
		}
		draining = false;
		emit("drain");
	}
	function length() {
		return queue.length;
	}
	return {
		enqueue,
		clear,
		length,
		on,
		onHandlerEvent
	};
}
//#endregion
//#region src/scope.ts
/**
* Creates a scope that closes when `parent` aborts.
*
* @example
* const scope = createScope(options.signal)
* scope.defer(() => track.stop())
* await scope.close('Recording finished')
*/
function createScope(parent, onCleanupError = console.error) {
	const controller = new AbortController();
	const cleanups = [];
	const settled = Promise.withResolvers();
	async function runCleanups() {
		for (const cleanup of cleanups.splice(0).reverse()) try {
			await cleanup();
		} catch (error) {
			onCleanupError(error);
		}
		settled.resolve();
	}
	function close(reason) {
		if (!controller.signal.aborted) {
			controller.abort(reason);
			runCleanups();
		}
		return settled.promise;
	}
	parent?.addEventListener("abort", () => void close(parent.reason), {
		once: true,
		signal: controller.signal
	});
	if (parent?.aborted) close(parent.reason);
	return {
		signal: controller.signal,
		closed: settled.promise,
		defer(cleanup) {
			if (controller.signal.aborted) Promise.resolve().then(cleanup).catch(onCleanupError);
			else cleanups.push(cleanup);
		},
		child() {
			return createScope(controller.signal, onCleanupError);
		},
		close
	};
}
//#endregion
//#region src/stream.ts
/**
* The optional cancellation handler owns resources that feed this readable output.
* `strategy` measures unread values. Writers check `desiredSize` because a push source cannot wait.
*/
function createPushStream(onCancel, strategy) {
	let closed = false;
	let controller = null;
	return {
		stream: new ReadableStream({
			start(ctrl) {
				controller = ctrl;
			},
			cancel(reason) {
				closed = true;
				onCancel?.(reason);
			}
		}, strategy),
		write(value) {
			if (!controller || closed) return;
			controller.enqueue(value);
		},
		close() {
			if (!controller || closed) return;
			closed = true;
			controller.close();
		},
		error(err) {
			if (!controller || closed) return;
			closed = true;
			controller.error(err);
		},
		isClosed() {
			return closed;
		},
		desiredSize() {
			return controller?.desiredSize ?? null;
		}
	};
}
async function readStream(stream, handler) {
	const reader = stream.getReader();
	try {
		while (true) {
			const { value, done } = await reader.read();
			if (done) break;
			await handler(value);
		}
	} finally {
		reader.releaseLock();
	}
}
//#endregion
//#region src/task.ts
function reasonFrom(value, fallback) {
	if (typeof value === "string") return value;
	if (value instanceof Error) return value.message;
	return fallback;
}
/**
* Creates a task that settles by an explicit call.
*
* @example
* const task = createTask<string>({ signal: parent.signal, timeoutMs: 5000 })
* task.defer(() => socket.close())
* task.finish('ok')
* await task.done // => { status: 'finished', value: 'ok' }
*/
function createTask(options = {}) {
	if (options.timeoutMs !== void 0 && (!Number.isFinite(options.timeoutMs) || options.timeoutMs < 0)) throw new Error("Task timeout must be finite and nonnegative");
	const controller = new AbortController();
	const completion = Promise.withResolvers();
	const cleanups = [];
	const cleaned = Promise.withResolvers();
	const onCleanupError = options.onCleanupError ?? console.error;
	const parent = options.signal;
	let settled = false;
	let timer;
	async function runCleanups() {
		for (const cleanup of cleanups.splice(0).reverse()) try {
			await cleanup();
		} catch (error) {
			onCleanupError(error);
		}
		cleaned.resolve();
	}
	function settle(outcome) {
		if (settled) return;
		settled = true;
		clearTimeout(timer);
		parent?.removeEventListener("abort", onParentAbort);
		if (outcome.status !== "finished") controller.abort(outcome.status === "failed" ? outcome.error : outcome.reason);
		completion.resolve(outcome);
		runCleanups();
	}
	function onParentAbort() {
		settle({
			status: "cancelled",
			reason: reasonFrom(parent?.reason, "Parent task closed")
		});
	}
	const task = {
		signal: controller.signal,
		done: completion.promise,
		closed: cleaned.promise,
		get settled() {
			return settled;
		},
		defer(cleanup) {
			if (settled) Promise.resolve().then(cleanup).catch(onCleanupError);
			else cleanups.push(cleanup);
		},
		finish: (value) => settle({
			status: "finished",
			value
		}),
		cancel: (reason) => settle({
			status: "cancelled",
			reason
		}),
		fail: (error) => settle({
			status: "failed",
			error
		})
	};
	if (parent?.aborted) {
		onParentAbort();
		return task;
	}
	parent?.addEventListener("abort", onParentAbort, { once: true });
	if (options.timeoutMs !== void 0) timer = setTimeout(() => task.fail(/* @__PURE__ */ new Error("Task timed out")), options.timeoutMs);
	return task;
}
/**
* Runs `work` as one task. The returned value finishes the task, and a thrown error fails it.
* A call to `cancel` or `fail` inside `work` wins over its later return.
*
* @example
* const task = runTask(async ({ signal }) => fetch(url, { signal }).then(response => response.text()))
* const outcome = await task.done
*/
function runTask(work, options = {}) {
	const task = createTask(options);
	if (task.settled) return task;
	(async () => {
		try {
			task.finish(await work(task));
		} catch (cause) {
			task.fail(cause instanceof Error ? cause : new Error("Task failed", { cause }));
		}
	})();
	return task;
}
/**
* Merges outcomes. A failure wins over a cancellation, and a cancellation wins over success.
*
* @example
* worstOutcome([{ status: 'finished', value: undefined }, { status: 'cancelled', reason: 'Closed' }])
* // => { status: 'cancelled', reason: 'Closed' }
*/
function worstOutcome(outcomes) {
	const failed = outcomes.find((outcome) => outcome.status === "failed");
	if (failed) return failed;
	return outcomes.find((outcome) => outcome.status === "cancelled") ?? {
		status: "finished",
		value: void 0
	};
}
//#endregion
export { createLatest, createPushStream, createQueue, createScope, createTask, readStream, runTask, worstOutcome };
